/**
 * Authentication is a seam, not a feature.
 *
 * The configuration comes from the backend at runtime, never from the build, which is what makes
 * one image run in every environment.
 *
 * Kinde is the odd one out and has to be: its SDK publishes a hook, and a hook cannot be called
 * from the `useMemo` the other two are built in. So it mounts as a component and hands the same
 * `AuthContextValue` back through a render prop — which keeps the seam identical for everything
 * above it, at the cost of one early return here.
 */

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { useAuthConfig } from '~/api/queries';
import { setAccessTokenSource } from '~/api/client';
import { localAuth } from './local';
import { KindeGate, KindeSignIn } from './kinde';
import { jwtAuth, readSession } from './jwt';
import { SignIn } from './SignIn';
import type { AuthContextValue } from './types';

const loading: AuthContextValue = {
  status: 'loading',
  user: null,
  getAccessToken: async () => null,
  login: () => undefined,
  logout: () => undefined,
};

const AuthContext = createContext<AuthContextValue>(loading);

export function AuthProvider({ children }: { children: ReactNode }) {
  const config = useAuthConfig();
  // Storage is not reactive, so the session is held here and re-read only when it changes.
  const [session, setSession] = useState(readSession);

  const value = useMemo<AuthContextValue>(() => {
    if (config.isPending) return loading;
    if (config.data?.provider === 'jwt') {
      return jwtAuth(session, { onSignOut: () => setSession(null) });
    }
    return localAuth({
      id: 'local',
      displayName: 'Local user',
      email: null,
      roles: ['researcher'],
    });
  }, [config.isPending, config.data?.provider, session]);

  // Before the token source is installed, because under Kinde it is the bridge inside the gate
  // that installs it — the SDK owns the token and this component cannot reach it.
  if (!config.isPending && config.data?.provider === 'kinde') {
    return (
      <KindeGate
        config={config.data.kinde}
        render={(kinde) =>
          kinde.status === 'unauthenticated' ? (
            <KindeSignIn onSignIn={() => kinde.login()} />
          ) : (
            <AuthContext.Provider value={kinde}>{children}</AuthContext.Provider>
          )
        }
      />
    );
  }

  // Installed during render, not in an effect: effects run after the children have mounted and
  // their queries have already fired, so an effect leaves the first read of every page load
  // unauthenticated — which, before the API required a principal on reads, looked like working
  // software.
  //
  // And not at all while the provider is still unknown. `loading` hands back a source that
  // returns null, and installing it would replace the client's own default — which reads the
  // stored session synchronously — for exactly the window in which the first reads happen.
  if (value.status !== 'loading') {
    setAccessTokenSource(value.getAccessToken);
  }

  // Nothing behind the form is worth rendering, so it replaces the shell rather than covering it.
  if (value.status === 'unauthenticated') {
    return <SignIn onSignedIn={() => setSession(readSession())} />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
